/** Extensions we convert to CSV before upload. */
const SPREADSHEET_RE = /\.(xlsx|xlsm|xls)$/i;

export function isSpreadsheetFile(file: File): boolean {
  return SPREADSHEET_RE.test(file.name);
}

/**
 * Turn a spreadsheet into the CSV the pipeline actually understands.
 *
 * A .xlsx is a ZIP of XML, and the worker hands non-PDF script files to Gemini
 * as text/plain — so uploading one raw would feed the model binary noise and
 * quietly produce nonsense scenes. Converting here means what lands in storage
 * is always text the extraction step can read, and no allowlist, worker, or
 * prompt has to know spreadsheets exist.
 *
 * Every sheet is included (a script split across tabs must not silently lose
 * half its scenes); sheet names are kept as headers when there's more than one,
 * which the extraction prompt reads as ordinary prose.
 */
export async function spreadsheetFileToCsv(file: File): Promise<File> {
  // ~1MB of parser for a rarely-taken path — load it only when it's needed.
  const XLSX = await import("xlsx");

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  const sheets = workbook.SheetNames.map((name) => ({
    name,
    csv: XLSX.utils.sheet_to_csv(workbook.Sheets[name]!).trim(),
  })).filter((s) => s.csv !== "");

  if (sheets.length === 0) {
    throw new Error("That spreadsheet is empty — nothing to read.");
  }

  const csv =
    sheets.length === 1
      ? sheets[0]!.csv
      : sheets.map((s) => `# Sheet: ${s.name}\n${s.csv}`).join("\n\n");

  return new File([csv], file.name.replace(SPREADSHEET_RE, ".csv"), { type: "text/csv" });
}

/**
 * The file to actually upload for a picked script: spreadsheets become CSV,
 * everything else passes through untouched.
 */
export async function prepareScriptFile(file: File): Promise<File> {
  return isSpreadsheetFile(file) ? spreadsheetFileToCsv(file) : file;
}
